import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactElement, RefObject } from "react";
import { Images, Plus, SlidersHorizontal, Upload, X } from "lucide-react";
import {
  ApiError,
  api,
  type Catalog,
  type Generation,
  type GenerationMode,
  type ImageAsset,
  type Model,
  type ParameterValues,
  type Provider,
} from "../api";
import { AsyncButton, ErrorNote, IconButton, describeError } from "./ui";
import { LibraryPicker } from "./LibraryPicker";
import { ModelPicker } from "./ModelPicker";
import {
  ParameterControls,
  countChangedFromDefaults,
  defaultsFor,
  prepareForSubmit,
} from "./ParameterControls";
import { readPreferences, recordModelUse, usePreferences } from "../hooks/usePreferences";

/**
 * What the next frame will be made from. It lives in Studio rather than here, because "use as
 * input" and "branch from here" are actions taken on a frame in the stream, and they have to be
 * able to fill the composer.
 */
export interface ComposerDraft {
  attachments: ImageAsset[];
  maskImageId?: string;
  parentGenerationId?: string;
}

export const emptyDraft: ComposerDraft = { attachments: [] };

/**
 * Per model, so switching model and back restores what you had (§11.1). The values live in the one
 * preferences key rather than a `darkroom.parameters.<model>` key each (§20), which is why the
 * coercion that used to sit here does not: usePreferences already refuses anything but scalars.
 *
 * Record<string, T> indexes as T without `noUncheckedIndexedAccess`, and an absent model is the
 * normal case, so the lookup is widened rather than trusted.
 */
function storedValues(byModel: Record<string, ParameterValues>, modelId: string): ParameterValues | undefined {
  const lookup: Record<string, ParameterValues | undefined> = byModel;
  return lookup[modelId];
}

/** Closes a popover or menu on Escape or a press outside its anchor. */
function useDismiss(open: boolean, anchor: RefObject<HTMLElement | null>, close: () => void): void {
  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    const onPointerDown = (event: MouseEvent) => {
      const element = anchor.current;
      if (element && event.target instanceof Node && !element.contains(event.target)) close();
    };

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("mousedown", onPointerDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("mousedown", onPointerDown);
    };
  }, [open, anchor, close]);
}

function supportsMode(model: Model, mode: GenerationMode): boolean {
  return mode === "edit" ? model.capabilities.supports_edit : model.capabilities.supports_generate;
}

interface ComposerProps {
  catalog: Catalog;
  boardId: string;
  draft: ComposerDraft;
  onDraftChange: (draft: ComposerDraft) => void;
  /** Called once the generation has been accepted, so the board can reload and start polling. */
  onSubmitted: () => void;
  /**
   * A frame to repeat. Studio has already put its images in the draft; the composer takes the
   * rest (model, parameters, prompt) and then calls onRecycled so the same frame can be asked for
   * again later.
   */
  recycled: Generation | null;
  onRecycled: () => void;
}

export function Composer({
  catalog,
  boardId,
  draft,
  onDraftChange,
  onSubmitted,
  recycled,
  onRecycled,
}: ComposerProps): ReactElement {
  const [preferences, setPreferences] = usePreferences();

  const models = useMemo(
    () => catalog.providers.flatMap((provider) => provider.models),
    [catalog],
  );
  const providerByModel = useMemo(() => {
    const map = new Map<string, Provider>();
    for (const provider of catalog.providers) {
      for (const model of provider.models) map.set(model.id, provider);
    }
    return map;
  }, [catalog]);

  // Mode is inferred from the draft, never chosen (§11.1).
  const mode: GenerationMode = draft.attachments.length > 0 ? "edit" : "generate";

  const fallbackModel = useMemo(
    () =>
      models.find(
        (model) => providerByModel.get(model.id)?.configured && model.capabilities.supports_generate,
      ) ?? models[0],
    [models, providerByModel],
  );

  // Both initialisers run once, on the first render, so they read the preferences of that moment
  // deliberately: a later change to the remembered model must not reset the open composer.
  const [modelId, setModelId] = useState(() => {
    const remembered = preferences.last_model_id;
    if (remembered !== null) {
      const match = models.find((model) => model.id === remembered);
      // A model that has been withdrawn from the catalog, or that cannot do what the draft is
      // asking for, is not the one to open on.
      if (match && supportsMode(match, mode)) return match.id;
    }
    return fallbackModel?.id ?? "";
  });

  const [values, setValues] = useState<ParameterValues>(() => {
    const opening = models.find((model) => model.id === modelId);
    if (!opening) return {};
    return storedValues(preferences.parameters_by_model, opening.id) ?? defaultsFor(opening, mode);
  });

  const [prompt, setPrompt] = useState("");
  const [parametersOpen, setParametersOpen] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string> | undefined>(undefined);
  const [error, setError] = useState<{ title: string; detail?: string } | null>(null);

  const model = models.find((candidate) => candidate.id === modelId);
  const provider = model ? providerByModel.get(model.id) : undefined;

  /** Choosing a model is also the act of remembering it for next time (§20). */
  function chooseModel(nextModelId: string): void {
    setModelId(nextModelId);
    setPreferences({ last_model_id: nextModelId });
  }

  // Mode is deliberately not a dependency of the re-seed below: attaching an image flips the
  // mode, and discarding someone's parameter edits at that moment would be rude. prepareForSubmit
  // drops whatever the new mode does not accept.
  const modeRef = useRef(mode);
  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);

  const seededFor = useRef(modelId);
  useEffect(() => {
    if (seededFor.current === modelId) return;
    seededFor.current = modelId;
    const next = models.find((candidate) => candidate.id === modelId);
    if (!next) return;
    // Read outside the subscription: this effect must fire when the model changes and at no other
    // time, and every keystroke in a parameter control writes the preferences.
    const remembered = storedValues(readPreferences().parameters_by_model, next.id);
    setValues(remembered ?? defaultsFor(next, modeRef.current));
  }, [modelId, models]);

  const promptInput = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (!recycled) return;
    setError(null);
    setFieldErrors(undefined);
    setPrompt(recycled.prompt);

    const target = models.find((candidate) => candidate.id === recycled.model_id);
    if (target) {
      // Set ahead of the model change so the re-seed above does not replace these with whatever
      // was last remembered for the model. Not written to the preferences either: repeating an
      // old frame should not rewrite what a new one starts from.
      seededFor.current = target.id;
      setModelId(target.id);
      setValues({ ...defaultsFor(target, recycled.mode), ...recycled.parameters });
    } else {
      setError({
        title: `${recycled.model_display_name} is no longer available.`,
        detail: "The prompt and reference images are restored. Choose another model to continue.",
      });
    }

    onRecycled();
    promptInput.current?.focus();
  }, [recycled, models, onRecycled]);

  function updateValues(next: ParameterValues): void {
    setValues(next);
    if (!model) return;
    const modelKey = model.id;
    setPreferences((previous) => ({
      parameters_by_model: { ...previous.parameters_by_model, [modelKey]: next },
    }));
  }

  // ── Attachments ────────────────────────────────────────────────────────────────────────

  const fileInput = useRef<HTMLInputElement>(null);
  const [attachOpen, setAttachOpen] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const attachAnchor = useRef<HTMLDivElement>(null);
  const closeAttach = useCallback(() => setAttachOpen(false), []);
  useDismiss(attachOpen, attachAnchor, closeAttach);

  /** How many more references the model takes; 0 means the reason has been shown. */
  function roomForMore(): number {
    if (!model) return 0;
    const limit = Math.max(1, model.capabilities.max_input_images);
    const room = limit - draft.attachments.length;
    if (room <= 0) {
      setError({
        title: `${model.display_name} takes at most ${limit} reference ${limit === 1 ? "image" : "images"}.`,
        detail: "Remove one before adding another, or switch to a model that takes more.",
      });
      return 0;
    }
    return room;
  }

  async function addFiles(chosen: File[]): Promise<void> {
    if (chosen.length === 0 || !model) return;
    setError(null);

    const room = roomForMore();
    if (room === 0) return;

    const uploaded: ImageAsset[] = [];
    try {
      // One at a time on purpose: a handful of 25 MiB uploads fired at once is a worse experience
      // than a queue, and the first failure should stop the rest.
      for (const file of chosen.slice(0, room)) {
        uploaded.push(await api.uploadImage(file));
      }
    } catch (uploadFailure) {
      setError(describeError(uploadFailure));
    }

    if (uploaded.length > 0) {
      onDraftChange({ ...draft, attachments: [...draft.attachments, ...uploaded] });
    }
  }

  function openPicker(): void {
    setAttachOpen(false);
    setError(null);
    if (roomForMore() > 0) setPickerOpen(true);
  }

  function addFromLibrary(picked: ImageAsset[]): void {
    setPickerOpen(false);
    const fresh = picked.filter((image) => !draft.attachments.some((item) => item.id === image.id));
    if (fresh.length === 0) return;

    // Same lineage rule as "use as input": a frame from this board becomes the parent. Another
    // board's frame cannot be one, and the server rejects it.
    const lineage = fresh.find((image) => image.generation_id && image.board_id === boardId);
    onDraftChange({
      ...draft,
      attachments: [...draft.attachments, ...fresh],
      parentGenerationId: draft.parentGenerationId ?? lineage?.generation_id,
    });
  }

  function removeAttachment(imageId: string): void {
    const attachments = draft.attachments.filter((image) => image.id !== imageId);
    onDraftChange({
      ...draft,
      attachments,
      maskImageId: draft.maskImageId === imageId ? undefined : draft.maskImageId,
      // With nothing attached the next frame is not descended from anything.
      parentGenerationId: attachments.length === 0 ? undefined : draft.parentGenerationId,
    });
  }

  // ── Parameters popover ─────────────────────────────────────────────────────────────────

  const popoverAnchor = useRef<HTMLDivElement>(null);
  const closeParameters = useCallback(() => setParametersOpen(false), []);
  useDismiss(parametersOpen, popoverAnchor, closeParameters);

  const changedCount = model ? countChangedFromDefaults(model, mode, values) : 0;

  // ── Submit ─────────────────────────────────────────────────────────────────────────────

  const canDoMode = model ? supportsMode(model, mode) : false;
  const configured = provider?.configured ?? false;

  const alternatives = useMemo(
    () =>
      models
        .filter(
          (candidate) =>
            candidate.id !== modelId &&
            (providerByModel.get(candidate.id)?.configured ?? false) &&
            supportsMode(candidate, mode),
        )
        .slice(0, 3),
    [models, providerByModel, modelId, mode],
  );

  let blockedReason: string | undefined;
  if (!model) {
    blockedReason = "No model is available. Check the provider keys on the server.";
  } else if (!configured) {
    blockedReason =
      provider?.unavailable_reason ??
      `${provider?.display_name ?? "This provider"} has no API key configured.`;
  } else if (!canDoMode) {
    blockedReason =
      mode === "edit"
        ? `${model.display_name} cannot edit an image. Remove the reference images, or switch model.`
        : `${model.display_name} only works from a reference image. Attach one, or switch model.`;
  } else if (prompt.trim() === "") {
    blockedReason = "Write a prompt first.";
  }

  const submitting = useRef(false);
  async function submit(): Promise<void> {
    if (!model || blockedReason || submitting.current) return;
    submitting.current = true;
    setError(null);
    setFieldErrors(undefined);

    try {
      await api.createGeneration(boardId, {
        model_id: model.id,
        mode,
        prompt: prompt.trim(),
        parameters: prepareForSubmit(model, mode, values),
        // The mask is carried separately, so it must not appear in the inputs as well.
        input_image_ids: draft.attachments
          .filter((image) => image.id !== draft.maskImageId)
          .map((image) => image.id),
        mask_image_id: draft.maskImageId,
        parent_generation_id: draft.parentGenerationId,
      });
      // Accepted by the server, so this counts as a use; a rejected request does not (§20).
      recordModelUse(model.id);
      setPrompt("");
      onSubmitted();
    } catch (failure) {
      if (failure instanceof ApiError && failure.errors) {
        // Per-parameter messages belong on the controls that produced them, not in a banner.
        setFieldErrors(failure.errors);
        setParametersOpen(true);
      } else {
        setError(describeError(failure));
      }
    } finally {
      submitting.current = false;
    }
  }

  const accepted = model?.capabilities.accepted_input_mime_types ?? [];

  return (
    <div className="composer">
      {error && <ErrorNote title={error.title} detail={error.detail} />}

      {model && (!configured || !canDoMode) && (
        <div className="notice row wrap">
          <span>{blockedReason}</span>
          {alternatives.length > 0 && <span className="stamp">Switch to</span>}
          {alternatives.map((candidate) => (
            <button
              key={candidate.id}
              type="button"
              className="btn ghost"
              onClick={() => chooseModel(candidate.id)}
            >
              {candidate.display_name}
            </button>
          ))}
        </div>
      )}

      <div className="composer-attachments">
        {draft.attachments.map((image) => (
          <div
            key={image.id}
            className={image.id === draft.maskImageId ? "attachment is-mask" : "attachment"}
          >
            <img src={image.thumb_url ?? image.content_url} alt="" />
            <button
              type="button"
              className="attachment-remove"
              aria-label="Remove reference image"
              onClick={() => removeAttachment(image.id)}
            >
              <X size={12} />
            </button>
          </div>
        ))}

        <div className="popover-anchor" ref={attachAnchor}>
          <IconButton
            onClick={() => setAttachOpen((open) => !open)}
            label="Add reference image"
            disabled={!model}
            disabledReason="Pick a model first."
          >
            <Plus size={16} />
          </IconButton>

          {attachOpen && (
            <div className="menu" role="menu">
              <button
                type="button"
                role="menuitem"
                className="list-row"
                onClick={() => {
                  setAttachOpen(false);
                  fileInput.current?.click();
                }}
              >
                <Upload size={15} aria-hidden />
                <span className="grow">Upload</span>
              </button>
              <button type="button" role="menuitem" className="list-row" onClick={openPicker}>
                <Images size={15} aria-hidden />
                <span className="grow">Select from library</span>
              </button>
            </div>
          )}
        </div>
        {/* The button above is the real control; this only exists to open the file dialog. */}
        <input
          ref={fileInput}
          className="sr-only"
          tabIndex={-1}
          aria-hidden
          type="file"
          multiple
          accept={accepted.length > 0 ? accepted.join(",") : "image/*"}
          onChange={(event) => {
            const chosen = Array.from(event.target.files ?? []);
            // Cleared straight away, so choosing the same file twice in a row still fires a change.
            event.target.value = "";
            void addFiles(chosen);
          }}
        />

        {model?.capabilities.supports_mask && draft.attachments.length > 0 && (
          <div className="row">
            <span className="stamp">Mask</span>
            <div className="segmented" role="group" aria-label="Which attachment is the mask">
              <button
                type="button"
                className={draft.maskImageId === undefined ? "is-selected" : undefined}
                aria-pressed={draft.maskImageId === undefined}
                onClick={() => onDraftChange({ ...draft, maskImageId: undefined })}
              >
                None
              </button>
              {draft.attachments.map((image, index) => (
                <button
                  key={image.id}
                  type="button"
                  className={draft.maskImageId === image.id ? "is-selected" : undefined}
                  aria-pressed={draft.maskImageId === image.id}
                  onClick={() => onDraftChange({ ...draft, maskImageId: image.id })}
                >
                  {index + 1}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <textarea
        ref={promptInput}
        className="composer-input"
        value={prompt}
        placeholder={
          mode === "edit"
            ? "Describe the change to make to the attached image…"
            : "Describe the next frame…"
        }
        aria-label={mode === "edit" ? "Edit instruction" : "Prompt"}
        onChange={(event) => setPrompt(event.target.value)}
        onKeyDown={(event) => {
          if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
            event.preventDefault();
            void submit();
          }
        }}
      />

      <div className="composer-controls">
        <ModelPicker
          catalog={catalog}
          selectedModelId={modelId}
          mode={mode}
          onSelect={chooseModel}
        />

        <div className="popover-anchor" ref={popoverAnchor}>
          <button
            type="button"
            className="btn"
            aria-expanded={parametersOpen}
            disabled={!model}
            onClick={() => setParametersOpen((open) => !open)}
          >
            <SlidersHorizontal size={14} aria-hidden />
            Parameters
            {changedCount > 0 && ` (${changedCount} changed)`}
          </button>

          {/* Below 760px the stylesheet turns this into a bottom sheet, because a popover anchored
              to a control narrower than its contents is unusable on a phone (§21.1). */}
          {parametersOpen && model && (
            <div className="popover" role="dialog" aria-label="Parameters">
              <div className="popover-head">
                <h3 className="section-title">Parameters</h3>
                <div className="row">
                  <button
                    type="button"
                    className="btn ghost"
                    onClick={() => updateValues(defaultsFor(model, mode))}
                  >
                    Reset to defaults
                  </button>
                  <IconButton onClick={() => setParametersOpen(false)} label="Close parameters">
                    <X size={14} />
                  </IconButton>
                </div>
              </div>
              <ParameterControls
                model={model}
                mode={mode}
                values={values}
                errors={fieldErrors}
                onChange={updateValues}
              />
            </div>
          )}
        </div>

        <span className="composer-spacer" />
        {/* The shortcut is a reminder, not a control: it goes at the first sign of crowding. */}
        <span className="stamp hide-mobile">⌘⏎</span>
        <AsyncButton
          className="btn primary"
          onClick={submit}
          disabled={blockedReason !== undefined}
          disabledReason={blockedReason}
        >
          {mode === "edit" ? "Edit frame" : "Generate frame"}
        </AsyncButton>
      </div>

      {pickerOpen && model && (
        <LibraryPicker
          room={Math.max(1, model.capabilities.max_input_images) - draft.attachments.length}
          attachedIds={new Set(draft.attachments.map((image) => image.id))}
          acceptedMimeTypes={accepted}
          maxBytes={model.capabilities.max_input_bytes}
          onPick={addFromLibrary}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  );
}
